import { get } from '../../apiClient';
import { API_ENDPOINTS } from '../../config';

const quotationDashboardService = {
  /**
   * Get KPIs for quotation dashboard
   * @param {Object} filters - Filter parameters
   * @returns {Promise<Object>} KPI data
   */
  getKPIs: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams();
      
      if (filters.owner) queryParams.append('owner', filters.owner);
      if (filters.team) queryParams.append('team', filters.team);
      if (filters.product) queryParams.append('product', filters.product);
      if (filters.region) queryParams.append('region', filters.region);
      if (filters.dateRange) queryParams.append('dateRange', filters.dateRange);
      if (filters.startDate) queryParams.append('startDate', filters.startDate);
      if (filters.endDate) queryParams.append('endDate', filters.endDate);

      const url = queryParams.toString() 
        ? `${API_ENDPOINTS.CRM.QUOTATION_DASHBOARD}/kpis?${queryParams.toString()}`
        : `${API_ENDPOINTS.CRM.QUOTATION_DASHBOARD}/kpis`;

      const response = await get(url);
      
      if (response && response.responseCode === 200) {
        return {
          success: true,
          kpis: response.data
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to retrieve KPIs',
          kpis: {
            totalQuotations: 0,
            acceptedCount: 0,
            rejectedCount: 0,
            expiredCount: 0,
            sentCount: 0,
            seenCount: 0,
            draftCount: 0,
            expiringSoon: 0,
            orderCount: 0,
            orderedCount: 0,
            acceptanceRate: 0,
            quoteToOrderRate: 0
          }
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to retrieve KPIs',
        kpis: {
          totalQuotations: 0,
          acceptedCount: 0,
          rejectedCount: 0,
          expiredCount: 0,
          sentCount: 0,
          seenCount: 0,
          draftCount: 0,
          expiringSoon: 0,
          orderCount: 0,
          orderedCount: 0,
          acceptanceRate: 0,
          quoteToOrderRate: 0
        }
      };
    }
  },

  /**
   * Get specific KPIs for quotations
   * @param {Object} filters - Filter parameters
   * @returns {Promise<Object>} Quotation KPI data
   */
  getQuotationKPIs: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams();
      
      if (filters.owner) queryParams.append('owner', filters.owner);
      if (filters.team) queryParams.append('team', filters.team);
      if (filters.product) queryParams.append('product', filters.product);
      if (filters.region) queryParams.append('region', filters.region);
      if (filters.dateRange) queryParams.append('dateRange', filters.dateRange);
      if (filters.startDate) queryParams.append('startDate', filters.startDate);
      if (filters.endDate) queryParams.append('endDate', filters.endDate);

      const url = queryParams.toString() 
        ? `${API_ENDPOINTS.CRM.QUOTATION_DASHBOARD}/quotation-kpis?${queryParams.toString()}`
        : `${API_ENDPOINTS.CRM.QUOTATION_DASHBOARD}/quotation-kpis`;

      const response = await get(url);
      
      if (response && response.responseCode === 200) {
        return {
          success: true,
          kpis: response.data
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to retrieve quotation KPIs',
          kpis: {
            totalQuotations: 0,
            acceptedCount: 0,
            rejectedCount: 0,
            expiredCount: 0,
            sentCount: 0,
            seenCount: 0,
            draftCount: 0,
            expiringSoon: 0
          }
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to retrieve quotation KPIs',
        kpis: {
          totalQuotations: 0,
          acceptedCount: 0,
          rejectedCount: 0,
          expiredCount: 0,
          sentCount: 0,
          expiringSoon: 0
        }
      };
    }
  },

  /**
   * Get quotation list with filters and pagination
   * @param {Object} filters - Filter parameters
   * @param {Object} pagination - Pagination parameters
   * @returns {Promise<Object>} Quotation list with pagination
   */
  getQuotationList: async (filters = {}, pagination = {}) => {
    try {
      const queryParams = new URLSearchParams();
      
      // Pagination params
      if (pagination.tab) queryParams.append('tab', pagination.tab);
      if (pagination.page) queryParams.append('page', pagination.page);
      if (pagination.limit) queryParams.append('limit', pagination.limit);
      
      // Filter params
      if (filters.search) queryParams.append('search', filters.search);
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.owner) queryParams.append('owner', filters.owner);
      if (filters.team) queryParams.append('team', filters.team);
      if (filters.product) queryParams.append('product', filters.product);
      if (filters.region) queryParams.append('region', filters.region);
      if (filters.dateRange) queryParams.append('dateRange', filters.dateRange);
      if (filters.startDate) queryParams.append('startDate', filters.startDate);
      if (filters.endDate) queryParams.append('endDate', filters.endDate);

      const url = `${API_ENDPOINTS.CRM.QUOTATION_DASHBOARD}/list?${queryParams.toString()}`;

      const response = await get(url);
      
      if (response && response.responseCode === 200) {
        return {
          success: true,
          quotations: response.data?.data || [],
          pagination: response.data?.pagination || {}
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to retrieve quotation list',
          quotations: [],
          pagination: {}
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to retrieve quotation list',
        quotations: [],
        pagination: {}
      };
    }
  },

  /**
   * Get opportunities ready to close with their quotations
   * @param {Object} filters - Filter parameters
   * @returns {Promise<Object>} Opportunities with quotations data
   */
  getCloseOpportunities: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams();
      
      // Filter params
      if (filters.tab) queryParams.append('tab', filters.tab);
      if (filters.owner) queryParams.append('owner', filters.owner);
      if (filters.dateRange) queryParams.append('dateRange', filters.dateRange);
      if (filters.startDate) queryParams.append('startDate', filters.startDate);
      if (filters.endDate) queryParams.append('endDate', filters.endDate);
      if (filters.search) queryParams.append('search', filters.search);
      if (filters.status) queryParams.append('status', filters.status);

      const url = `${API_ENDPOINTS.CRM.QUOTATION_DASHBOARD}/close-opportunities?${queryParams.toString()}`;

      const response = await get(url);
      
      if (response && response.responseCode === 200) {
        return {
          success: true,
          opportunities: response.data || []
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to retrieve close opportunities',
          opportunities: []
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to retrieve close opportunities',
        opportunities: []
      };
    }
  },

  /**
   * Get closed opportunities with won/lost values
   * @param {Object} filters - Filter parameters
   * @returns {Promise<Object>} Closed opportunities data with won/lost values
   */
  getClosedOpportunities: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams();
      
      // Filter params
      if (filters.tab) queryParams.append('tab', filters.tab);
      if (filters.owner) queryParams.append('owner', filters.owner);
      if (filters.dateRange) queryParams.append('dateRange', filters.dateRange);
      if (filters.startDate) queryParams.append('startDate', filters.startDate);
      if (filters.endDate) queryParams.append('endDate', filters.endDate);
      if (filters.search) queryParams.append('search', filters.search);
      if (filters.status) queryParams.append('status', filters.status);

      const url = `${API_ENDPOINTS.CRM.QUOTATION_DASHBOARD}/closed-opportunities?${queryParams.toString()}`;

      const response = await get(url);
      
      if (response && response.responseCode === 200) {
        return {
          success: true,
          closedOpportunities: response.data || {}
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to retrieve closed opportunities',
          closedOpportunities: {}
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to retrieve closed opportunities',
        closedOpportunities: {}
      };
    }
  },

  /**
   * Close opportunity as won or lost
   * @param {String} opportunityId - Opportunity ID
   * @param {String} closeType - 'won' or 'lost'
   * @param {Object} closeData - Additional data for closing
   * @returns {Promise<Object>} Result of the close operation
   */
  closeOpportunity: async (opportunityId, closeType, closeData = {}) => {
    try {
      // Import opportunity service dynamically to avoid circular dependencies
      const opportunityService = (await import('../../crm/opportunities/opportunityService')).default;
      
      // Determine the stage based on close type
      const newStage = closeType === 'won' ? 'closed_won' : 'closed_lost';
      
      // Call the opportunity service to move the opportunity to the closed stage
      const result = await opportunityService.moveOpportunity(opportunityId, newStage);
      
      return result;
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to close opportunity'
      };
    }
  },

  /**
   * Get total KPI stats for close won/lost opportunities without any filters
   * @returns {Promise<Object>} Total KPI stats for close won/lost opportunities
   */
  getCloseWonLostStats: async () => {
    try {
      const url = `${API_ENDPOINTS.CRM.QUOTATION_DASHBOARD}/closeWonLost-stats`;

      const response = await get(url);
      
      if (response && response.responseCode === 200) {
        return {
          success: true,
          stats: response.data || {
            totalOpportunities: 0,
            totalWonValue: 0,
            totalLostValue: 0,
            totalWonCount: 0,
            totalLostCount: 0,
            avgDealSize: 0,
            highProbabilityOpportunities: 0
          }
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to retrieve close won/lost statistics',
          stats: {
            totalOpportunities: 0,
            totalWonValue: 0,
            totalLostValue: 0,
            totalWonCount: 0,
            totalLostCount: 0,
            avgDealSize: 0,
            highProbabilityOpportunities: 0
          }
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to retrieve close won/lost statistics',
        stats: {
          totalOpportunities: 0,
          totalWonValue: 0,
          totalLostValue: 0,
          totalWonCount: 0,
          totalLostCount: 0,
          avgDealSize: 0,
          highProbabilityOpportunities: 0
        }
      };
    }
  },

  /**
   * Export full Quotation Dashboard as a formatted Excel (.xlsx) workbook (KPIs + list)
   * @param {Object} filters - Active dashboard filters
   * @param {Array<string>} selectedIds - Optional selected row IDs
   * @returns {Promise<Object>} Success or error result
   */
  exportDashboardDetail: async (filters = {}, selectedIds = []) => {
    try {
      const queryParams = new URLSearchParams();
      if (filters.owner) queryParams.append('owner', filters.owner);
      if (filters.team) queryParams.append('team', filters.team);
      if (filters.product) queryParams.append('product', filters.product);
      if (filters.region) queryParams.append('region', filters.region);
      if (filters.dateRange) queryParams.append('dateRange', filters.dateRange);
      if (filters.startDate) queryParams.append('startDate', filters.startDate);
      if (filters.endDate) queryParams.append('endDate', filters.endDate);
      if (filters.search) queryParams.append('search', filters.search);
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.tab) queryParams.append('tab', filters.tab);
      if (Array.isArray(selectedIds) && selectedIds.length > 0) {
        queryParams.append('selectedIds', selectedIds.join(','));
      }

      const queryString = queryParams.toString();
      const url = `${API_ENDPOINTS.CRM.QUOTATION_DASHBOARD}/export${queryString ? `?${queryString}` : ''}`;

      const blob = await get(url, { responseType: 'blob' });

      const filename = `Quotation_Dashboard_${new Date().toISOString().slice(0, 10)}.xlsx`;

      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to export quotation dashboard'
      };
    }
  }
};

export default quotationDashboardService;